// Single source of truth for the core requests warmed by the inline <head>
// script (see build-inline-script.ts) and handed back to TanStack Query (see
// consume-prefetch.ts). The query strings mirror the TypedDocumentString
// documents used by the real query hooks; operations.test.ts fails if they drift.
//
// This module must stay dependency-free: rsbuild.config.ts imports it at build
// time to generate the inline script, so it cannot pull in React, the gql
// client, or any browser-only code.

export const PREFETCH_GLOBAL_KEY = '__TYRO_PREFETCH__';

export const MY_AUTH_DETAILS_OPERATION = 'myAuthDetails';
export const SETTINGS_VALUES_OPERATION = 'catalogue_systemSettingsValues';
export const ACADEMIC_NAMESPACES_OPERATION = 'core_academicNamespaces';

// Wire value of Catalogue_SystemSettingsViewType.Personal. Kept as a literal so
// this module stays import-free; operations.test.ts asserts it matches the enum.
const PERSONAL_SETTINGS_VIEW_TYPE = 'PERSONAL';

export interface PrefetchOperation {
  operationName: string;
  query: string;
  variables?: Record<string, unknown>;
}

const myAuthDetailsQuery = `
  query myAuthDetails {
    myAuthDetails {
      id
      email
      name
      defaultProfileId
      activeProfileId
      isEmulatingAsTyroUser
      profiles {
        id
        nickName
        avatarUrl
        tenant {
          tenant
          name
          imgUrl
          tenantType
        }
        profileType {
          name
          description
          userType
        }
        permissionIds
        partyId
        tenantId
        blocked
      }
    }
  }
`;

const settingsValuesQuery = `
  query catalogue_systemSettingsValues($filter: Catalogue_SystemSettingsFilter!) {
    catalogue_systemSettingsValues(filter: $filter) {
      attendance_subjectGroupAttendance_customSubjectGroupAttendanceCodes
      attendance_shareAttendanceNote_enabled
      security_dataPrivacy_confirmBeforeShowingSensitiveData
      security_logout_inactiveTimeUntilLogoutMinutes
      substitution_display_showAbsenceReason
      substitution_display_teacherDisplayNameType
      people_display_teacherDisplayNameType
      noticeboard_display_alwaysShowPinned
      communications_sms_studentSms
      schoolActivity_attendance_defaultAttendanceCode
      timetable_twoWeekTimetable_enabled
      timetable_construction_defaultToTableViewInBlockCores
      language_translation_enableSwitching
      language_translation_glossary
      featureFlag_settings_peapod
      featureFlag_attendance_sessionAttendance
      featureFlag_rulesEngine_rulesEnginePlus
      featureFlag_assessments_stateCba
      featureFlag_forms_newFormsSection
      featureFlag_security_advancedPermissions
      featureFlag_studentOptions_fullFeature
      featureFlag_userAccess_sds
      groupTags_groupsMenu_showProgrammeStages
      groupTags_groupsMenu_showYears
      groupTags_groupsMenu_showHouse
      groupTags_programmeStage_enabledStages
      security_summerMode_enabled
      behaviour_quickActions_list
      social_thirdParties_facebook
      social_thirdParties_instagram
      social_thirdParties_twitter
      attendance_signInOut_collectedBy
      attendance_overdue_numberOfDaysWidgetShows
      fees_feeForm_hideAbsorbFees
    }
  }
`;

const academicNamespacesQuery = `
  query core_academicNamespaces {
    core_academicNamespaces {
      academicNamespaceId
      type
      name
      year
      description
      isActiveDefaultNamespace
      startDate
      endDate
    }
  }
`;

export const PREFETCH_OPERATIONS: PrefetchOperation[] = [
  {
    operationName: MY_AUTH_DETAILS_OPERATION,
    query: myAuthDetailsQuery,
  },
  {
    operationName: SETTINGS_VALUES_OPERATION,
    query: settingsValuesQuery,
    variables: { filter: { type: PERSONAL_SETTINGS_VIEW_TYPE, module: [] } },
  },
  {
    operationName: ACADEMIC_NAMESPACES_OPERATION,
    query: academicNamespacesQuery,
  },
];
